import { faPlain, toman } from 'utilities/helper/format'

/**
 * The three interest fields every loan-creating screen has to send, derived in
 * one place from whatever the کارمزد modal handed back.
 *
 * **`interest_rate` is a FRACTION on the wire.** The backend repays
 * `amount × (1 + interest_rate)` (`Loan.total_repayment_amount`), so «۱ درصد»
 * has to leave the client as `0.01`, not `1`.
 *
 * Four screens built this payload by hand and two of them passed the picker's
 * number straight through, which multiplied every fee by a hundred. Accepting
 * a loan request with «۱ درصد» granted a **100 percent** loan: 30,000,000
 * تومان repaid as 3 × 20,000,000 (loan 36058, 1405/05/28). The «مبلغ ثابت» tab
 * was unaffected — it never went through `interest_rate`.
 *
 * Derive it here rather than at each call site; a fifth screen must not get a
 * fifth chance to drop the division.
 */
export interface ILoanInterestFields {
    interest_type: string
    interest_rate: number
    fixed_interest_amount: number
}

export const FEE_TYPE_PERCENTAGE = 'percentage'

export const loanInterestFields = (
    feeType: string | undefined,
    feeValue: number | undefined | null
): ILoanInterestFields => {
    const value = Number(feeValue ?? 0) || 0

    if (feeType === FEE_TYPE_PERCENTAGE) {
        return {
            interest_type: FEE_TYPE_PERCENTAGE,
            interest_rate: value / 100,
            fixed_interest_amount: 0,
        }
    }

    return {
        interest_type: feeType ?? 'fixed',
        interest_rate: 0,
        fixed_interest_amount: value,
    }
}

/**
 * The mirror of `loanInterestFields`: those same three fields turned back into
 * the one line a receipt shows.
 *
 * The two «تایید نهایی» receipts printed `interest_rate` raw next to «درصد».
 * That field is the fraction this module deliberately produces, so a کارمزد of
 * ۱٫۵٪ reached the manager as «0.015 درصد» (pwa#71). The «مبلغ ثابت» branch was
 * wrong in the other direction and had been all along: `loanInterestFields`
 * zeroes `interest_rate` for a fixed fee and parks the number in
 * `fixed_interest_amount`, so every fixed کارمزد rendered as «0 تومان» — the
 * receipt said the manager was charging nothing.
 *
 * Round before formatting: `0.035 × 100` is `3.5000000000000004` in binary
 * floating point, and «۳٫۵۰۰۰۰۰۰۰۰۰۰۰۰۰۰۰۴ درصد» is its own bug report.
 */
export const loanFeeLabel = (
    fields: Partial<ILoanInterestFields> | undefined | null
): string => {
    if (fields?.interest_type === FEE_TYPE_PERCENTAGE) {
        const percent = (Number(fields.interest_rate) || 0) * 100
        return `${faPlain.format(Math.round(percent * 100) / 100)} درصد`
    }

    return toman(Number(fields?.fixed_interest_amount) || 0)
}
